import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Copy, RefreshCw, UserPlus, Save } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Avatar } from 'components/ui/avatar';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useToast } from 'components/ui/use-toast';
import * as TeamsModule from 'modules/teams';
import { useAuthContext } from 'modules/auth';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { GroupProfile, GroupProfileSkeleton } from './GroupProfile';

const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode; danger?: boolean }> = ({
  title,
  action,
  children,
  danger,
}) => (
  <section className={`overflow-hidden rounded-lg border bg-card ${danger ? 'border-destructive/40' : 'border-border'}`}>
    <div className="flex items-center justify-between gap-2 px-5 py-4">
      <h2 className={`text-sm font-semibold ${danger ? 'text-destructive' : 'text-foreground'}`}>{title}</h2>
      {action}
    </div>
    <div className="border-t border-border px-5 py-5">{children}</div>
  </section>
);

/** There is no `subject` field, and no soft-archive endpoint — the danger zone really deletes. */
export const TeamSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state: authState } = useAuthContext();
  const user = authState.user;

  const { team, isLoading, refetch } = TeamsModule.Hooks.useSingle({ id: teamId || '', enabled: Boolean(teamId) });
  const { joinToken } = TeamsModule.Hooks.useJoinToken({ teamId: teamId || '', enabled: Boolean(teamId) });
  const updateTeam = TeamsModule.Hooks.useUpdate();
  const deleteTeam = TeamsModule.Hooks.useDelete();
  const generateToken = TeamsModule.Hooks.useGenerateJoinToken();
  const updateRole = TeamsModule.Hooks.useUpdateMemberRole();

  const canEdit = usePermission(PERMISSIONS.TEAMS_EDIT);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [seeded, setSeeded] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (team && !seeded) {
      setName(team.name);
      setDescription(team.description ?? '');
      setSeeded(true);
    }
  }, [team, seeded]);

  if (isLoading || !team) {
    return <GroupProfileSkeleton />;
  }

  const dirty = name !== team.name || description !== (team.description ?? '');
  const admins = team.members.filter((m) => m.role === 'admin');
  const promotable = team.members.filter((m) => m.role === 'member');

  const handleSave = async () => {
    if (!teamId || !name.trim()) return;
    await updateTeam.mutateAsync({ id: teamId, name: name.trim(), description: description.trim() || undefined });
    refetch();
  };

  const handleCopy = () => {
    if (!joinToken?.code) return;
    navigator.clipboard.writeText(joinToken.code);
    toast.success(t('group_code_copied'));
  };

  const handleRegenerate = async () => {
    if (!teamId) return;
    await generateToken.mutateAsync(teamId);
    toast.success(t('group_code_regenerated'));
  };

  const handleAddCoTeacher = async (userId: string) => {
    if (!teamId) return;
    await updateRole.mutateAsync({ teamId, userId, role: 'admin' });
    refetch();
  };

  const handleDelete = async () => {
    if (!teamId) return;
    await deleteTeam.mutateAsync(teamId);
    navigate('/app/teams');
  };

  return (
    <GroupProfile team={team} activeTab="settings">
      <div className="mx-auto max-w-3xl space-y-5">
        {/* Group details */}
        <Section
          title={t('group_settings_details')}
          action={
            canEdit && dirty ? (
              <Button size="sm" className="h-8" onClick={handleSave} disabled={updateTeam.isPending || !name.trim()}>
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {t('save_changes')}
              </Button>
            ) : undefined
          }
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="group-name">{t('group_settings_name')}</Label>
              <Input id="group-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="group-desc">{t('group_settings_description')}</Label>
              <Input
                id="group-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canEdit}
                placeholder={t('groups_description_placeholder')}
              />
            </div>
          </div>
        </Section>

        {/* Join code */}
        <Section title={t('group_settings_join_code')}>
          <div className="flex flex-wrap items-center gap-3">
            {joinToken?.code ? (
              <>
                <span className="rounded-lg bg-muted px-3 py-2 font-mono text-lg font-bold tracking-wider text-foreground">
                  {joinToken.code}
                </span>
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                  {t('group_code_copy')}
                </Button>
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={handleRegenerate} disabled={generateToken.isPending}>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    {t('group_code_regenerate')}
                  </Button>
                )}
                <span className="ml-auto text-xs text-muted-foreground">{t('group_code_hint')}</span>
              </>
            ) : (
              <>
                <span className="text-sm text-muted-foreground">{t('group_code_none')}</span>
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={handleRegenerate} disabled={generateToken.isPending}>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    {t('group_code_generate')}
                  </Button>
                )}
              </>
            )}
          </div>
        </Section>

        {/* Co-teachers */}
        <Section
          title={t('group_settings_coteachers')}
          action={
            canEdit && promotable.length > 0 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="xs" className="gap-1.5">
                    <UserPlus className="h-3.5 w-3.5" />
                    {t('group_add_coteacher')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
                  {promotable.map((m) => (
                    <DropdownMenuItem key={m.userId} onClick={() => handleAddCoTeacher(m.userId)}>
                      {m.user?.name || m.user?.email || t('teams.unknown_user')}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : undefined
          }
        >
          <div className="space-y-1">
            {admins.map((m) => {
              const isOwner = m.userId === team.createdBy;
              return (
                <div key={m.userId} className="flex items-center gap-3 rounded-lg px-1 py-2">
                  <Avatar name={m.user?.name} src={m.user?.avatarUrl} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {m.user?.name || m.user?.email || t('teams.unknown_user')}
                    {m.userId === user?.id && <span className="ml-1.5 text-xs text-muted-foreground">({t('teams.you')})</span>}
                  </span>
                  <span
                    className={
                      isOwner
                        ? 'rounded-md bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground/70'
                        : 'rounded-md border border-border px-2 py-0.5 text-xs font-medium text-foreground/70'
                    }
                  >
                    {isOwner ? t('group_role_owner') : t('group_role_coteacher')}
                  </span>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Danger zone */}
        {canEdit && (
          <Section title={t('group_danger_title')} danger>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">{t('group_danger_desc')}</p>
              <Button variant="outline" size="sm" className="shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => setDeleteOpen(true)}>
                {t('group_danger_action')}
              </Button>
            </div>
          </Section>
        )}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('teams.delete_team_title', { name: team.name })}
        description={t('group_danger_confirm')}
        confirmLabel={t('group_danger_action')}
        cancelLabel={t('cancel')}
        destructive
        loading={deleteTeam.isPending}
        onConfirm={handleDelete}
      />
    </GroupProfile>
  );
};

export default TeamSettingsPage;
